import React, { useState } from "react";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatIDR, buildWhatsAppUrl } from "@/lib/api";

const CartSheet = () => {
  const { items, open, setOpen, updateQty, removeItem, total, clearCart } = useCart();
  const [note, setNote] = useState("");

  const checkout = () => {
    if (items.length === 0) return;
    const url = buildWhatsAppUrl(items, total, note);
    window.open(url, "_blank");
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[#0a0a0a] border-l border-white/10 text-white flex flex-col p-0"
        data-testid="cart-sheet"
      >
        <SheetHeader className="px-6 py-5 border-b border-white/10">
          <SheetTitle className="font-display text-white text-2xl tracking-tight flex items-center gap-2">
            <ShoppingBag className="w-5 h-5 text-accent-wl" /> Keranjang
          </SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto no-scrollbar px-6 py-4 space-y-4">
          {items.length === 0 ? (
            <div className="h-full flex flex-col items-center justify-center text-center py-20">
              <ShoppingBag className="w-12 h-12 text-white/20 mb-4" />
              <p className="text-white/50 font-body">Keranjang masih kosong.</p>
            </div>
          ) : (
            items.map((it) => (
              <div
                key={it.id}
                data-testid={`cart-item-${it.id}`}
                className="flex gap-4 border border-white/10 rounded-xl p-3 bg-white/[0.02]"
              >
                <img src={it.image} alt={it.name} className="w-20 h-20 object-cover rounded-lg" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs uppercase tracking-wider text-accent-wl">{it.brand}</p>
                  <h4 className="font-body font-semibold text-sm truncate">{it.name}</h4>
                  <p className="text-sm text-white/70 mt-1">{formatIDR(it.price)}</p>
                  <div className="flex items-center gap-3 mt-2">
                    <button onClick={() => updateQty(it.id, -1)} className="w-7 h-7 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10" data-testid={`qty-minus-${it.id}`}>
                      <Minus className="w-3 h-3" />
                    </button>
                    <span className="w-5 text-center text-sm">{it.qty}</span>
                    <button onClick={() => updateQty(it.id, 1)} className="w-7 h-7 rounded-full border border-white/20 flex items-center justify-center hover:bg-white/10" data-testid={`qty-plus-${it.id}`}>
                      <Plus className="w-3 h-3" />
                    </button>
                    <button onClick={() => removeItem(it.id)} className="ml-auto text-white/40 hover:text-red-400" data-testid={`remove-${it.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t border-white/10 px-6 py-5 space-y-4">
            <Textarea
              placeholder="Catatan pesanan (opsional)..."
              value={note}
              onChange={(e) => setNote(e.target.value)}
              className="bg-white/5 border-white/10 text-white resize-none h-16"
              data-testid="cart-note"
            />
            <div className="flex items-center justify-between">
              <span className="text-white/60 font-body">Total</span>
              <span className="font-display font-bold text-2xl">{formatIDR(total)}</span>
            </div>
            <Button
              onClick={checkout}
              data-testid="whatsapp-checkout-btn"
              className="w-full bg-gradient-wl text-white hover:opacity-90 font-body font-semibold rounded-full h-12 text-base transition-opacity"
            >
              Checkout via WhatsApp
            </Button>
            <button onClick={clearCart} className="w-full text-center text-xs text-white/40 hover:text-white/70" data-testid="clear-cart">
              Kosongkan keranjang
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;
